import { Method } from "axios";
import { ActionKeyPair, IService } from "@/shared/hooks/useCustomQuery";
import api from "@/shared/api/api";
import { IPack, GetPacksParams, CreatePackPayload, Pack } from "./packs/types";
import { PaginatedResponse } from "@/features/profiles/services/profiles/types";
import { Category } from "@/features/categories/model";

export interface Preset {
  id: string;
  packId: string;
  name: string;
  isFree: boolean;
  isBlackAndWhite: boolean;
  cover: string;
  lut: string;
  published: boolean;
  createdAt: string;
  createdById: string;
  lastUpdatedAt: string;
  lastUpdatedById: string | null;
  firebaseId: string;
  displayOrder: number | null;
  CategoriesPresets: Array<{
    categoryId: string;
    presetId: string;
    createdAt: string;
    createdById: string;
    category: Category;
  }>;
  translations: Array<{
    id: string;
    presetId: string;
    languageCode: string;
    about: string;
    createdAt: string;
    createdById: string;
    lastUpdatedAt: string;
    lastUpdatedById: string | null;
  }>;
  galleries: Array<{
    id: string;
    presetId: string;
    after: string;
    before: string;
    displayOrder: number;
    createdAt: string;
    createdById: string;
    lastUpdatedAt: string;
    lastUpdatedById: string | null;
  }>;
}

export type CategoryPreset = {
  categoryId: string;
  presetId: string;
  createdAt: string;
  createdById: string;
  category: Category;
};
export default class PackService implements IService {
  [key: string]: ActionKeyPair<unknown, unknown>;

  getPacks: ActionKeyPair<GetPacksParams, PaginatedResponse<IPack>> = {
    key: "get-packs",
    actionBuilder: (params) => async () => {
      return api({
        url: "/v1/packs",
        method: "GET" as Method,
        params,
      });
    },
  };

  deletePack: ActionKeyPair<{ id: string }, void> = {
    key: "delete-pack",
    actionBuilder: ({ id }) => async () => {
      return api({
        url: `/v1/packs/${id}`,
        method: "DELETE" as Method,
      });
    },
  };

  createPack: ActionKeyPair<
    { data: Omit<CreatePackPayload, "cover"> & { cover: File } },
    IPack
  > = {
      key: "create-pack",
      actionBuilder:
        ({ data }) =>
          async () => {
            const formData = new FormData();
            const { cover, ...rest } = data;
            formData.append("data", JSON.stringify(rest));
            formData.append("cover", cover);
            return api({
              url: `/v1/packs`,
              method: "POST" as Method,
              data: formData,
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
          },
    };

  updatePack: ActionKeyPair<
    {
      id: string;
      data: Omit<CreatePackPayload, "cover"> & { cover?: File };
    },
    IPack
  > = {
      key: "update-pack",
      actionBuilder:
        ({ id, data }) =>
          async () => {
            const formData = new FormData();
            const { cover, ...rest } = data;
            formData.append("data", JSON.stringify(rest));
            if (cover) {
              formData.append("cover", cover);
            }
            return api({
              url: `/v1/packs?id=${id}`,
              method: "POST" as Method,
              data: formData,
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
          },
    };

  getPackPresets: ActionKeyPair<{ packId: string }, Preset[]> = {
    key: `get-pack-presets-${Date.now()}`,
    actionBuilder:
      ({ packId }) =>
        async () => {
          return api({
            url: `/v1/packs/${packId}/presets`,
            method: "GET" as Method,
          });
        },
  };

  getPackById: ActionKeyPair<{ id: string }, Pack> = {
    key: `get-pack-by-id-${Date.now()}`,
    actionBuilder:
      ({ id }) =>
        async () => {
          return api({
            url: `/v1/packs/${id}`,
            method: "GET" as Method,
          });
        },
  };

  static Instance = new PackService();
}
